/**
 * PeriodsTab — Períodos de nómina con detalle expandible y acciones.
 */
import { todayISO } from '../../../lib/utils'
import { useState } from 'react'
import { Plus, Calculator, CheckCircle, Banknote, Download, Eye, ChevronDown, ChevronRight, ExternalLink, AlertTriangle, Loader2, FileText, FileArchive, Landmark, ShieldCheck, CalendarDays } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Link } from 'react-router-dom'
import { usePeriods, useCreatePeriod, useCalculatePeriod, useApprovePeriod, usePayPeriod, usePayslips, usePeriodInputs, useCreatePeriodInput, useUpdatePeriodInput, useEmployees, useIndicatorPeriods, type PayrollPeriod } from '../hooks/usePayroll'
import { useAccounts } from '../../accounting/hooks/useAccounting'
import { PayslipModal } from './PayslipsTab'
import { usePermission } from '../../../hooks/usePermission'
import { useTenantLocale, formatNumber } from '../../../lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import { ConfirmModal, useConfirmModal } from '../../../components/ConfirmModal'
import { extractErrorMessage } from '../../../lib/errorUtils'
import apiClient, { apiDownload } from '../../../lib/apiClient'
import { useVisorPdf } from '@/components/VisorPdf'
import { PreviredResumenModal } from './PreviredResumenModal'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { PAYROLL_PERIOD_STATUS } from '../../../components/ds/statusCatalogs'
import { Modal } from '@/components/ds/Modal'
import { Button } from '@/components/ds/Button'

/** Trae un PDF del ERP como binario: lleva el JWT, así que no sirve un enlace. */
const pdfDelErp = (ruta: string, timeout?: number) => async () =>
  (await apiClient.get(ruta, { responseType: 'blob', timeout })).data as Blob

/** El PDF del período arma todas las liquidaciones en un request: pasa los 30 s
 * del cliente con ~30 personas. 120 s es el tope de gunicorn. */
const TIMEOUT_PDF_DEL_PERIODO_MS = 120_000


const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

export default function PeriodsTab() {
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()
  const [expandedId, setExpandedId] = useState('')
  // El botón de Previred abre el resumen; la descarga vive dentro del modal.
  const [previred, setPrevired] = useState<{ year: number; month: number } | null>(null)

  const { data, isLoading } = usePeriods()
  const { mutate: createPeriod } = useCreatePeriod()

  const periods = data?.periods || []

  const fmt = (v: string | number) => {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return `$${formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)}`
  }

  const handleNewPeriod = () => {
    const now = new Date()
    createPeriod({ year: now.getFullYear(), month: now.getMonth() + 1 }, {
      onSuccess: () => toast.success('Período creado'),
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al crear período')),
    })
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-content-muted">{periods.length} períodos</span>
        {can('payroll.periods', 'create') && (
          <button onClick={handleNewPeriod}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={14} /> Nuevo período
          </button>
        )}
      </div>

      <DataTable<PayrollPeriod>
        columns={[
          { key: 'expandir', header: '', width: 32,
            render: p => (
              <span className="text-content-subtle">
                {expandedId === p.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            ) },
          { key: 'periodo', header: 'Período',
            // Ordena por año y mes, no por el texto: «Abril 2026» como cadena
            // pone abril antes que enero.
            sortable: true, sortValue: p => (p.year || 0) * 100 + (p.month || 0),
            render: p => (
              <span className="font-medium text-content">
                {MONTHS[(p.month || 1) - 1]} {p.year}
              </span>
            ) },
          { key: 'status', header: 'Estado', sortable: true,
            render: p => <StatusBadge status={p.status} catalog={PAYROLL_PERIOD_STATUS} /> },
          { key: 'payslips_count', header: 'Empleados', numeric: true,
            sortable: true, sortValue: p => p.payslips_count || 0,
            render: p => p.payslips_count || 0 },
          { key: 'total_gross', header: 'Total Bruto', numeric: true,
            sortable: true, sortValue: p => Number(p.total_gross || 0),
            render: p => fmt(p.total_gross) },
          { key: 'total_net', header: 'Total Líquido', numeric: true,
            sortable: true, sortValue: p => Number(p.total_net || 0),
            render: p => <span className="font-medium text-primary">{fmt(p.total_net)}</span> },
          { key: 'total_employer_cost', header: 'Costo Empleador', numeric: true,
            sortable: true, sortValue: p => Number(p.total_employer_cost || 0),
            render: p => <span className="text-content-muted">{fmt(p.total_employer_cost)}</span> },
          { key: 'acciones', header: 'Acciones', width: 112,
            // `stopPropagation` para que las acciones no expandan la fila de
            // paso: el clic de la fila es lo que abre el detalle.
            render: p => (
              <div onClick={e => e.stopPropagation()}>
                <PeriodActions period={p} toast={toast}
                  onVerPrevired={(year, month) => setPrevired({ year, month })} />
              </div>
            ) },
        ]}
        rows={periods}
        rowKey={p => p.id}
        loading={isLoading}
        empty={
          <EmptyState
            icon={CalendarDays}
            title="Sin períodos"
            hint="Cree el primer período de nómina para empezar a liquidar."
          />
        }
        onRowClick={p => setExpandedId(expandedId === p.id ? '' : p.id)}
        rowExpanded={p => expandedId === p.id}
        // Sin padding: `PeriodDetail` trae su propio layout, que es justamente
        // por lo que la celda del detalle no hereda el de `.cl-table td`.
        expandedRow={p => <PeriodDetail periodId={p.id} fmt={fmt} period={p} toast={toast} />}
        caption="Períodos de nómina"
      />

      {previred && (
        <PreviredResumenModal
          open
          year={previred.year}
          month={previred.month}
          onClose={() => setPrevired(null)}
        />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Period Actions ─────────────────────────────────────────────────────────

function PeriodActions({ period, toast, onVerPrevired }: {
  period: PayrollPeriod
  toast: any
  onVerPrevired?: (year: number, month: number) => void
}) {
  const { mutate: calculate, isPending: isCalc } = useCalculatePeriod(period.id)
  const { ver, visor } = useVisorPdf()
  const { mutate: approve, isPending: isAppr } = useApprovePeriod(period.id)
  const { mutate: pay, isPending: isPay } = usePayPeriod(period.id)
  const { confirm, modalProps } = useConfirmModal()
  const [payModalOpen, setPayModalOpen] = useState(false)
  const [aprobarAbierto, setAprobarAbierto] = useState(false)
  // Sin parámetros: todas las filas comparten la misma queryKey, así que la tabla
  // hace UNA sola llamada y cada fila busca su período en memoria.
  const { data: indicadores } = useIndicatorPeriods()
  const estadoIndicadores = indicadores?.periods.find(
    p => p.year === period.year && p.month === period.month,
  )

  const doCalculate = () => {
    calculate(undefined, {
      onSuccess: (r: any) => {
        const skipped: any[] = r.skipped || []
        if (skipped.length > 0) {
          const names = skipped.slice(0, 3).map((s) => s.employee).join(', ')
          const more = skipped.length > 3 ? ` y ${skipped.length - 3} más` : ''
          toast.warning(
            `${r.payslips_calculated} liquidaciones calculadas. ${skipped.length} empleado(s) omitido(s): ${names}${more}. Revise contrato y configuración previsional vigentes.`,
          )
        } else {
          toast.success(`${r.payslips_calculated} liquidaciones calculadas`)
        }
      },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  /** Calcular con indicadores faltantes se permite, pero avisando qué falta.
   *
   *  Decisión de producto: NO se bloquea el cálculo (hay meses que se cierran antes
   *  de que salga todo). Lo que no puede volver a pasar es que se calcule sin que
   *  nada lo advierta: julio 2026 se calculó con topes de 2024 y el error apareció
   *  recién al comparar contra Buk. */
  const handleCalculate = () => {
    const faltan = (estadoIndicadores?.indicators ?? []).filter(e => e.state === 'pending')
    if (!faltan.length) return doCalculate()

    const detalle = faltan.map(e => {
      if (!e.expected_date) return e.label
      const [, m, d] = e.expected_date.split('-')
      return `${e.label} (se espera el ${Number(d)}/${Number(m)})`
    }).join(' · ')

    confirm({
      message: `Faltan ${faltan.length} indicador${faltan.length !== 1 ? 'es' : ''} del período: ${detalle}. `
        + 'Si calculas ahora, los conceptos que dependen de esos valores usarán el último dato '
        + 'conocido y las liquidaciones podrían quedar con montos incorrectos. ¿Calcular igual?',
      variant: 'warning',
      confirmLabel: 'Calcular igual',
      onConfirm: doCalculate,
    })
  }

  const handleApprove = () => setAprobarAbierto(true)

  // Tres salidas y no dos (#312): volver a aprobar tras un recálculo NO debe
  // mandarle a cada trabajador un segundo correo por la misma liquidación.
  const aprobar = (notificarTrabajadores: boolean) => approve({ notificarTrabajadores }, {
    onSuccess: (r: any) => {
      setAprobarAbierto(false)
      if (r?.voucher_warning) {
        toast.warning(`Período aprobado, pero no se generó el voucher: ${r.voucher_warning}`)
      } else {
        toast.success(notificarTrabajadores
          ? 'Período aprobado y contabilizado. Se avisó a los trabajadores por correo.'
          : 'Período aprobado y contabilizado, sin aviso a los trabajadores.')
      }
    },
    onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
  })

  const handlePay = () => setPayModalOpen(true)

  const submitPay = (payload: { payment_date?: string; payment_account_id?: string; payment_reference?: string }) => {
    pay(payload, {
      onSuccess: (r: any) => {
        if (r?.payment_voucher_warning) {
          toast.warning(`Marcado como pagado, pero no se generó voucher: ${r.payment_voucher_warning}`)
        } else {
          toast.success('Período marcado como pagado y voucher de pago generado')
        }
        setPayModalOpen(false)
      },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  return (
    <div className="flex items-center justify-center gap-1">
      {['draft', 'calculated'].includes(period.status) && (
        <button onClick={handleCalculate} disabled={isCalc}
          title={isCalc ? 'Calculando liquidaciones…'
            : estadoIndicadores && !estadoIndicadores.ready_to_calculate
              ? `Faltan indicadores del período (${estadoIndicadores.missing.length}). Se puede calcular, con aviso.`
              : 'Calcular liquidaciones'}
          className="relative p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-500 disabled:opacity-60 disabled:cursor-wait">
          {isCalc ? <Loader2 size={15} className="animate-spin" /> : <Calculator size={15} />}
          {!isCalc && estadoIndicadores && !estadoIndicadores.ready_to_calculate && (
            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-warning-solid" />
          )}
        </button>
      )}
      {period.status === 'calculated' && (
        <button onClick={handleApprove} disabled={isAppr}
          title={isAppr ? 'Aprobando período…' : 'Aprobar período'}
          className="p-1.5 rounded-lg hover:bg-success-surface text-content-subtle hover:text-success-content-soft dark:text-emerald-300 disabled:opacity-60 disabled:cursor-wait">
          {isAppr ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
        </button>
      )}
      {period.status === 'approved' && (
        <button onClick={handlePay} disabled={isPay}
          title={isPay ? 'Procesando pago…' : 'Marcar como pagado'}
          className="p-1.5 rounded-lg hover:bg-success-surface text-content-subtle hover:text-success-content-soft dark:text-emerald-300 disabled:opacity-60 disabled:cursor-wait">
          {isPay ? <Loader2 size={15} className="animate-spin" /> : <Banknote size={15} />}
        </button>
      )}
      {period.status !== 'draft' && (
        <button onClick={() => apiDownload(`/payroll/periods/${period.id}/export/`, `libro_remuneraciones_${period.year}_${String(period.month).padStart(2,'0')}.xlsx`)}
          title="Descargar Libro de Remuneraciones"
          className="p-1.5 rounded-lg hover:bg-soft text-content-subtle">
          <Download size={15} />
        </button>
      )}
      {period.status !== 'draft' && (
        <button onClick={() => ver({
            titulo: `Liquidaciones de ${MONTHS[period.month - 1]} ${period.year}`,
            nombreArchivo: `liquidaciones_${period.year}_${String(period.month).padStart(2,'0')}.pdf`,
            cargar: pdfDelErp(`/payroll/periods/${period.id}/payslips-pdf/`, TIMEOUT_PDF_DEL_PERIODO_MS),
          })}
          title="Ver todas las liquidaciones en un solo PDF, con un índice por trabajador"
          className="p-1.5 rounded-lg hover:bg-soft text-content-subtle">
          {/* FileArchive y no FileText: cuatro descargas seguidas con íconos casi
              idénticos son cuatro botones que nadie distingue, y por eso RRHH pedía
              la descarga por lote que ya existía acá. El mismo ícono se usa en el
              botón con etiqueta de la pestaña Liquidaciones. */}
          <FileArchive size={15} />
        </button>
      )}
      {/* Abre el RESUMEN, no la descarga: el archivo salía sin que nadie supiera
          qué contenía, y el primer control era el validador de Previred. La
          descarga vive dentro del modal. */}
      {period.status !== 'draft' && (
        <button onClick={() => onVerPrevired?.(period.year, period.month)}
          title="Previred — ver el resumen por institución y descargar la planilla"
          className="p-1.5 rounded-lg hover:bg-soft text-content-subtle">
          <Landmark size={15} />
        </button>
      )}
      {period.status !== 'draft' && (
        <button onClick={() => apiDownload(`/payroll/seguro-social/?year=${period.year}&month=${period.month}`, `seguro_social_${period.year}_${String(period.month).padStart(2,'0')}.csv`)}
          title="Descargar Seguro Social Previsional (SSP · Reforma Ley 21.735)"
          className="p-1.5 rounded-lg hover:bg-soft text-content-subtle">
          <ShieldCheck size={15} />
        </button>
      )}
      {period.voucher && (
        <Link
          to={`/accounting/vouchers?highlight=${period.voucher}`}
          title={period.voucher_number ? `Comprobante ${period.voucher_number}` : 'Ver comprobante contable'}
          className="p-1.5 rounded-lg hover:bg-primary-50 text-content-subtle hover:text-primary-700"
        >
          <ExternalLink size={15} />
        </Link>
      )}
      <ConfirmModal {...modalProps} />
      <Modal
        open={aprobarAbierto}
        onClose={() => !isAppr && setAprobarAbierto(false)}
        size="md"
        title="¿Aprobar este período?"
        subtitle={`${period.year}-${String(period.month).padStart(2, '0')}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => setAprobarAbierto(false)} disabled={isAppr}>
              Cancelar
            </Button>
            <Button variant="secondary" onClick={() => aprobar(false)} loading={isAppr}>
              Aprobar sin notificar
            </Button>
            <Button variant="success-solid" onClick={() => aprobar(true)} loading={isAppr}>
              Aprobar y notificar
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-content-muted">
          <p>Las liquidaciones quedan aprobadas, se genera el comprobante contable y los PDF
            quedan en la carpeta de cada trabajador en el portal.</p>
          <p><strong className="text-content">Aprobar y notificar</strong> además envía a cada
            trabajador un correo avisando que su liquidación está disponible.</p>
          <p><strong className="text-content">Aprobar sin notificar</strong> no envía ese correo:
            es lo que corresponde al volver a aprobar un período ya publicado, después de recalcularlo.</p>
        </div>
      </Modal>
      {payModalOpen && (
        <PayPeriodModal
          period={period}
          onClose={() => setPayModalOpen(false)}
          onSubmit={submitPay}
          submitting={isPay}
        />
      )}
      {visor}
    </div>
  )
}

// ─── Pay Period Modal ───────────────────────────────────────────────────────

function PayPeriodModal({
  period, onClose, onSubmit, submitting,
}: {
  period: PayrollPeriod
  onClose: () => void
  onSubmit: (p: { payment_date?: string; payment_account_id?: string; payment_reference?: string }) => void
  submitting: boolean
}) {
  // Default fecha = hoy
  const today = todayISO()
  const [paymentDate, setPaymentDate] = useState(today)
  const [accountId, setAccountId] = useState('')
  const [reference, setReference] = useState('')

  // Solo cuentas imputables de tipo asset (bancarias/caja)
  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true', account_type: 'asset' })
  const bankAccounts = (accountsData?.accounts || []).filter((a: any) =>
    /banco|caja|cuenta corriente|cheques/i.test(a.name)
  )

  return (
    <Modal open onClose={onClose} title={<>Registrar pago — {period.year}-{String(period.month).padStart(2, '0')}</>} size="md"
      actions={<>
        <button onClick={onClose} disabled={submitting}
                className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft disabled:opacity-50">
          Cancelar
        </button>
        <button
          onClick={() => onSubmit({
            payment_date: paymentDate || undefined,
            payment_account_id: accountId || undefined,
            payment_reference: reference.trim() || undefined,
          })}
          disabled={submitting}
          className="px-4 py-2 text-sm rounded-lg bg-success-fill hover:bg-success-fill-hover text-white disabled:opacity-50"
        >
          {submitting ? 'Procesando…' : 'Marcar pagado'}
        </button>
      </>}
    >
      <div className="space-y-4 text-sm">
        <div>
          <label htmlFor="periodstab-fecha-de-pago" className="block text-xs font-medium text-content-muted mb-1">Fecha de pago</label>
          <input id="periodstab-fecha-de-pago"
            type="date"
            value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-divider bg-surface text-content"
          />
        </div>
        <div>
          <label htmlFor="periodstab-cuenta-bancaria-origen" className="block text-xs font-medium text-content-muted mb-1">Cuenta bancaria origen</label>
          <select id="periodstab-cuenta-bancaria-origen"
            value={accountId}
            onChange={e => setAccountId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-divider bg-surface text-content"
          >
            <option value="">Sin asignar (no se genera voucher de pago)</option>
            {bankAccounts.map((a: any) => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
          {bankAccounts.length === 0 && (
            <p className="mt-1 text-xs text-warning-content">
              No se encontraron cuentas bancarias. Configurar el plan de cuentas primero.
            </p>
          )}
        </div>
        <div>
          <label htmlFor="periodstab-n-transferencia-referencia" className="block text-xs font-medium text-content-muted mb-1">N° transferencia / referencia</label>
          <input id="periodstab-n-transferencia-referencia"
            type="text"
            value={reference}
            onChange={e => setReference(e.target.value)}
            placeholder="Ej. TX-2026-04-001"
            maxLength={80}
            className="w-full px-3 py-2 rounded-lg border border-divider bg-surface text-content"
          />
        </div>
      </div>
    </Modal>
  )
}

// ─── Period Detail (payslips table) ─────────────────────────────────────────

function PeriodDetail({ periodId, fmt, period, toast }: { periodId: string; fmt: (v: string | number) => string; period?: PayrollPeriod; toast: any }) {
  const { data: payslipsData, isLoading: loadingPayslips } = usePayslips({ period_id: periodId, page_size: 9999 })
  const { ver, visor } = useVisorPdf()
  const { data: inputsData } = usePeriodInputs({ period_id: periodId })
  const { data: employeesData } = useEmployees({ status: 'active', page_size: 9999 })
  const { mutate: createInput } = useCreatePeriodInput()
  // Sin esto un guardado rechazado (p. ej. un 403 por falta de permiso) no
  // dejaba ninguna señal: la celda conservaba lo tipeado y se leía como guardado.
  const onSaveError = (e: any) =>
    toast.error(extractErrorMessage(e, 'No se pudo guardar la novedad del período'))
  const [selectedPayslipId, setSelectedPayslipId] = useState<string>('')

  const payslips = payslipsData?.payslips || []
  const inputs = inputsData?.inputs || []
  const employees = employeesData?.employees || []
  const hasPayslips = payslips.length > 0
  const employeesWithContract = employees.filter((e: any) => e.has_current_contract).length
  // El aviso de "faltan por liquidar" solo aplica a períodos en curso (borrador/
  // calculado). En períodos ya aprobados/pagados (incl. históricos migrados) la
  // dotación pudo cambiar (ingresos/finiquitos posteriores) y compararla contra la
  // actual daría falsos positivos.
  const periodEditable = period?.status === 'draft' || period?.status === 'calculated'
  const missingCount = periodEditable ? employeesWithContract - payslips.length : 0

  const showAccountingWarning =
    period && period.status !== 'draft' && period.accounting_status === 'pending'

  // El período es editable mientras esté en borrador o calculado: ahí se puede
  // ajustar días/HE/bonos/dctos y volver a Calcular. Aprobado/pagado = solo lectura.
  const editable = !period || ['draft', 'calculated'].includes(period.status)

  // Sección de pre-cálculo (inputs por empleado). Se mantiene visible incluso
  // después de calcular, para poder modificar parámetros y recalcular.
  const preCalcSection = editable ? (
    <div>
      <div className="px-6 py-3 border-b border-divider">
        <p className="text-xs font-bold text-content-muted uppercase tracking-wider">
          {hasPayslips
            ? 'Pre-cálculo: ajuste días, horas extra, bonos o descuentos y vuelva a Calcular para actualizar'
            : 'Pre-cálculo: ingrese días, horas extra, bonos o descuentos por empleado antes de calcular'}
        </p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-divider">
            <th className="px-6 py-2 text-left text-xs font-semibold text-content-muted">Empleado</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-content-muted">RUT</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-content-muted" title="Días efectivamente trabajados (1-30)">Días</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-content-muted" title="Día hábil 50% + Domingo/Festivo 100%">HE (50% + 100%)</th>
            <th className="px-3 py-2 text-center text-xs font-semibold text-content-muted">Bonos/Dctos</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-content-muted">Notas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {/* ⚠️ Esta tabla tampoco se migra: sus filas las emite `PeriodInputRow`,
              un componente que consulta y escribe lo suyo. El `DataTable` recibe
              DATOS, no componentes de fila. Y además intercala una fila
              separadora a lo ancho para los empleados sin contrato. */}
          {/* Empleados con contrato — primero */}
          {employees.filter((e: any) => e.has_current_contract).map((emp: any) => {
            const input = inputs.find((i: any) => i.employee === emp.id)
            return (
              <PeriodInputRow key={emp.id} employee={emp} periodId={periodId}
                input={input} createInput={createInput} onSaveError={onSaveError} />
            )
          })}
          {/* Empleados sin contrato — al final, en gris y deshabilitados */}
          {employees.filter((e: any) => !e.has_current_contract).length > 0 && (
            <tr className="bg-surface">
              <td colSpan={6} className="px-6 py-2 text-xs italic text-content-subtle border-t border-divider">
                Empleados activos sin contrato vigente (no se incluirán en el cálculo):
              </td>
            </tr>
          )}
          {employees.filter((e: any) => !e.has_current_contract).map((emp: any) => (
            <tr key={emp.id} className="opacity-60">
              <td className="px-6 py-2 text-sm text-content-muted">
                {emp.full_name}
                <span className="ml-2 inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-warning-surface-strong text-warning-content">
                  sin contrato
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-content-subtle font-mono">{emp.rut}</td>
              <td className="px-3 py-2 text-xs text-right text-content-subtle">—</td>
              <td className="px-3 py-2 text-xs text-right text-content-subtle">—</td>
              <td className="px-3 py-2 text-xs text-center text-content-subtle">—</td>
              <td className="px-3 py-2 text-xs text-content-subtle italic">Cree un contrato para incluirlo</td>
            </tr>
          ))}
        </tbody>
      </table>
      {employees.length === 0 && (
        <p className="px-6 py-4 text-center text-sm text-content-subtle">Sin empleados activos.</p>
      )}
    </div>
  ) : null

  // Si no hay liquidaciones y el período no es editable, no hay nada que mostrar.
  if (!hasPayslips && !editable) {
    return <div className="bg-surface-muted border-b border-divider px-6 py-4 text-center text-sm text-content-subtle">Sin liquidaciones.</div>
  }

  return (
    <div className="bg-surface-muted border-b border-divider">
      {preCalcSection}

      {hasPayslips && editable && (
        <div className="px-6 py-3 border-t border-divider bg-surface">
          <p className="text-xs font-bold text-content-muted uppercase tracking-wider">Liquidaciones calculadas</p>
        </div>
      )}

      {loadingPayslips && (
        <div className="px-6 py-4 text-center text-sm text-content-subtle">Cargando liquidaciones...</div>
      )}

      {hasPayslips && (
      <>
      {showAccountingWarning && (
        <div className="px-6 py-3 bg-warning-surface border-b border-warning-border text-warning-content-strong text-sm flex items-center gap-2">
          <AlertTriangle size={16} className="shrink-0" />
          <span>
            <strong>Este período no se ha contabilizado.</strong> Configure las cuentas contables de Nómina en
            <code className="mx-1 px-1.5 py-0.5 rounded bg-warning-surface-strong">Contabilidad → Configuración</code>
            y reapruebe el período para generar el voucher automático.
          </span>
        </div>
      )}
      {missingCount > 0 && (
        <div className="px-6 py-2 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 text-xs flex items-center gap-2">
          <AlertTriangle size={14} className="shrink-0" />
          <span>
            {missingCount} {missingCount === 1 ? 'empleado con contrato no fue liquidado' : 'empleados con contrato no fueron liquidados'}.
            Verifique sus contratos y previsión.
          </span>
        </div>
      )}
      {(() => {
        // Los totales se suman una vez acá y no dentro del pie: repetir doce
        // `reduce` en el JSX hace que agregar una columna sea agregar otro.
        const suma = (campo: string) =>
          payslips.reduce((s: number, ps: any) => s + parseFloat(ps[campo] || '0'), 0)
        return (
          <DataTable<any>
            columns={[
              { key: 'employee_name', header: 'Empleado', sortable: true,
                render: ps => <span className="font-medium text-content">{ps.employee_name}</span> },
              { key: 'employee_rut', header: 'RUT', sortable: true,
                render: ps => <span className="text-xs text-content-subtle font-mono">{ps.employee_rut}</span> },
              { key: 'base_salary', header: 'Sueldo Base', numeric: true, sortable: true,
                sortValue: ps => Number(ps.base_salary), render: ps => fmt(ps.base_salary) },
              { key: 'gratification', header: 'Gratif.', numeric: true, sortable: true,
                sortValue: ps => Number(ps.gratification), render: ps => fmt(ps.gratification) },
              { key: 'total_bonuses', header: 'Bonos', numeric: true, sortable: true,
                sortValue: ps => Number(ps.total_bonuses), render: ps => fmt(ps.total_bonuses) },
              { key: 'overtime_hours', header: 'HH Extra', numeric: true, sortable: true,
                sortValue: ps => Number(ps.overtime_hours),
                render: ps => (Number(ps.overtime_hours) > 0 ? (
                  <span title={`${ps.overtime_hours}h × tasa = ${fmt(ps.overtime_amount)}`}>
                    {Number(ps.overtime_hours).toFixed(1)}h
                  </span>
                ) : '—') },
              { key: 'gross_salary', header: 'Bruto', numeric: true, sortable: true,
                sortValue: ps => Number(ps.gross_salary),
                render: ps => <span className="font-medium">{fmt(ps.gross_salary)}</span> },
              { key: 'afp_employee', header: 'AFP', numeric: true, sortable: true,
                sortValue: ps => Number(ps.afp_employee),
                render: ps => <span className="text-danger-solid">{fmt(ps.afp_employee)}</span> },
              { key: 'health_employee', header: 'Salud', numeric: true, sortable: true,
                sortValue: ps => Number(ps.health_employee),
                render: ps => <span className="text-danger-solid">{fmt(ps.health_employee)}</span> },
              { key: 'tax_amount', header: 'Impuesto', numeric: true, sortable: true,
                sortValue: ps => Number(ps.tax_amount),
                render: ps => <span className="text-danger-solid">{fmt(ps.tax_amount)}</span> },
              { key: 'total_deductions', header: 'Descuentos', numeric: true, sortable: true,
                sortValue: ps => Number(ps.total_deductions),
                render: ps => (
                  <span className="text-danger-content-soft font-medium">{fmt(ps.total_deductions)}</span>
                ) },
              { key: 'net_salary', header: 'Líquido', numeric: true, sortable: true,
                sortValue: ps => Number(ps.net_salary),
                render: ps => <span className="font-bold text-primary">{fmt(ps.net_salary)}</span> },
              { key: 'pdf', header: 'PDF', width: 56,
                render: ps => (
                  <span onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => ver({
                        titulo: `Liquidación ${ps.period_label ?? ''} · ${ps.employee_name ?? ''}`,
                        nombreArchivo: `liquidacion_${String(ps.employee_rut ?? '').replace(/\./g, '').replace('-', '_')}_${String(ps.period_label ?? '').replace('-', '_')}.pdf`,
                        cargar: pdfDelErp(`/payroll/payslips/${ps.id}/pdf/`),
                      })}
                      title="Ver la liquidación en PDF"
                      aria-label={`Ver la liquidación de ${ps.employee_name ?? ''}`}
                      className="inline-flex p-1.5 rounded-lg hover:bg-surface text-content-subtle hover:text-primary transition-colors">
                      <Eye size={14} aria-hidden="true" />
                    </button>
                  </span>
                ) },
            ]}
            rows={payslips}
            rowKey={ps => ps.id}
            minWidth={1320}
            surface={false}
            onRowClick={ps => setSelectedPayslipId(ps.id)}
            footerLabel={`Totales (${payslips.length} empleados)`}
            footer={{
              overtime_hours: `${payslips.reduce((s: number, ps: any) => s + Number(ps.overtime_hours || 0), 0).toFixed(1)}h`,
              gross_salary: fmt(suma('gross_salary')),
              afp_employee: <span className="text-danger-solid">{fmt(suma('afp_employee'))}</span>,
              health_employee: <span className="text-danger-solid">{fmt(suma('health_employee'))}</span>,
              tax_amount: <span className="text-danger-solid">{fmt(suma('tax_amount'))}</span>,
              total_deductions: (
                <span className="text-danger-content-soft">{fmt(suma('total_deductions'))}</span>
              ),
              net_salary: <span className="text-primary">{fmt(suma('net_salary'))}</span>,
            }}
            caption="Liquidaciones calculadas del período"
          />
        )
      })()}
      </>
      )}

      {selectedPayslipId && (
        <PayslipModal payslipId={selectedPayslipId} onClose={() => setSelectedPayslipId('')} fmt={fmt} />
      )}
      {visor}
    </div>
  )
}

// ─── Period Input Row (pre-cálculo por empleado) ────────────────────────────

function PeriodInputRow({ employee, periodId, input, createInput, onSaveError }: {
  employee: any; periodId: string; input?: any; createInput: any; onSaveError: (e: any) => void
}) {
  const [hours, setHours] = useState(input?.overtime_hours || '0')
  const [holidayHours, setHolidayHours] = useState(input?.overtime_holiday_hours || '0')
  const [daysWorked, setDaysWorked] = useState<number>(input?.days_worked ?? 30)
  const [notes, setNotes] = useState(input?.notes || '')
  const [bonusOverrides, setBonusOverrides] = useState<any[]>(input?.bonus_overrides || [])
  const [extraDeductions, setExtraDeductions] = useState<any[]>(input?.extra_deductions || [])
  const { mutate: update } = useUpdatePeriodInput(input?.id || '')
  const [extrasModalOpen, setExtrasModalOpen] = useState(false)

  const persist = (patch: any = {}) => {
    const payload = {
      overtime_hours: hours, notes,
      overtime_holiday_hours: holidayHours,
      days_worked: daysWorked,
      bonus_overrides: bonusOverrides,
      extra_deductions: extraDeductions,
      ...patch,
    }
    if (input?.id) {
      update(payload, { onError: onSaveError })
    } else {
      createInput({ period: periodId, employee: employee.id, ...payload }, { onError: onSaveError })
    }
  }

  const bonusCount = bonusOverrides.length
  const deductCount = extraDeductions.length

  return (
    <>
      <tr className="hover:bg-surface">
        <td className="px-6 py-2 text-sm font-medium text-content">{employee.full_name || `${employee.first_name} ${employee.last_name}`}</td>
        <td className="px-3 py-2 text-xs text-content-subtle font-mono">{employee.rut}</td>
        <td className="px-3 py-2 text-right">
          <input aria-label={`Días trabajados — ${employee.full_name || employee.first_name}`} type="number" min="1" max="30" step="1" value={daysWorked}
            onChange={e => setDaysWorked(Math.max(1, Math.min(30, Number(e.target.value) || 30)))}
            onBlur={() => persist()}
            className="w-14 px-2 py-1 text-sm text-right border border-divider rounded bg-surface focus:ring-1 focus:ring-primary" />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1">
            <input aria-label={`Horas extra en día hábil — ${employee.full_name || employee.first_name}`} type="number" min="0" step="0.5" value={hours}
              onChange={e => setHours(e.target.value)}
              onBlur={() => persist()}
              title="HE día hábil (recargo 50%)"
              className="w-14 px-2 py-1 text-sm text-right border border-divider rounded bg-surface focus:ring-1 focus:ring-primary" />
            <span className="text-[10px] text-content-subtle">+</span>
            <input aria-label={`Horas extra en día festivo — ${employee.full_name || employee.first_name}`} type="number" min="0" step="0.5" value={holidayHours}
              onChange={e => setHolidayHours(e.target.value)}
              onBlur={() => persist()}
              title="HE domingo/festivo (recargo 100%)"
              className="w-14 px-2 py-1 text-sm text-right border border-divider rounded bg-surface focus:ring-1 focus:ring-amber-400" />
          </div>
        </td>
        <td className="px-3 py-2 text-center">
          <button
            type="button"
            onClick={() => setExtrasModalOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border border-divider hover:bg-soft text-content-muted"
            title="Bonos variables + descuentos del mes"
          >
            {bonusCount > 0 && <span className="text-success-content-soft">+{bonusCount}</span>}
            {deductCount > 0 && <span className="text-danger-content-soft">-{deductCount}</span>}
            {bonusCount === 0 && deductCount === 0 && <span>Agregar</span>}
          </button>
        </td>
        <td className="px-3 py-2">
          <input aria-label={`Observaciones — ${employee.full_name || employee.first_name}`} type="text" value={notes} onChange={e => setNotes(e.target.value)}
            onBlur={() => persist()}
            placeholder="Obs."
            className="w-full px-2 py-1 text-xs border border-divider rounded bg-surface focus:ring-1 focus:ring-primary" />
        </td>
      </tr>
      {extrasModalOpen && (
        <PeriodInputExtrasModal
          employeeName={employee.full_name}
          initialBonuses={bonusOverrides}
          initialDeductions={extraDeductions}
          onClose={() => setExtrasModalOpen(false)}
          onSave={(b, d) => {
            setBonusOverrides(b)
            setExtraDeductions(d)
            persist({ bonus_overrides: b, extra_deductions: d })
            setExtrasModalOpen(false)
          }}
        />
      )}
    </>
  )
}

// ─── Period Input Extras Modal ──────────────────────────────────────────────

function PeriodInputExtrasModal({
  employeeName, initialBonuses, initialDeductions, onClose, onSave,
}: {
  employeeName: string
  initialBonuses: any[]
  initialDeductions: any[]
  onClose: () => void
  onSave: (bonuses: any[], deductions: any[]) => void
}) {
  const [bonuses, setBonuses] = useState(initialBonuses)
  const [deductions, setDeductions] = useState(initialDeductions)

  const addBonus = () => setBonuses(b => [...b, { name: '', amount: 0, is_imponible: true }])
  const removeBonus = (i: number) => setBonuses(b => b.filter((_, idx) => idx !== i))
  const updateBonus = (i: number, patch: any) => setBonuses(b => b.map((x, idx) => idx === i ? { ...x, ...patch } : x))

  const addDeduction = () => setDeductions(d => [...d, { name: '', amount: 0 }])
  const removeDeduction = (i: number) => setDeductions(d => d.filter((_, idx) => idx !== i))
  const updateDeduction = (i: number, patch: any) => setDeductions(d => d.map((x, idx) => idx === i ? { ...x, ...patch } : x))

  return (
    <tr>
      <td colSpan={6} className="px-0 py-0">
        <Modal open onClose={onClose} title={<>Bonos y descuentos del mes — {employeeName}</>} size="2xl"
          actions={<>
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">
              Cancelar
            </button>
            <button
              onClick={() => onSave(bonuses.filter(b => b.name.trim()), deductions.filter(d => d.name.trim()))}
              className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-700"
            >
              Guardar
            </button>
          </>}
        >
          <div className="space-y-6 overflow-y-auto">
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-content">Bonos variables del mes</h3>
                <button onClick={addBonus} className="text-xs px-2 py-1 rounded-md border border-divider hover:bg-soft text-content-muted">
                  + Agregar bono
                </button>
              </div>
              <p className="text-xs text-content-subtle mb-2">
                Estos bonos se suman SOBRE los bonos fijos asignados al empleado, solo en este período.
              </p>
              {bonuses.length === 0 && <p className="text-xs italic text-content-subtle">Sin bonos variables.</p>}
              {bonuses.map((b: any, i: number) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input aria-label={`Nombre del bono ${i + 1}`} type="text" placeholder="Nombre (ej. Bono cumpleaños)"
                    value={b.name} onChange={e => updateBonus(i, { name: e.target.value })}
                    className="flex-1 px-2 py-1 text-sm border border-divider rounded bg-surface" />
                  <input aria-label={`Monto del bono ${i + 1}`} type="number" min="0" placeholder="Monto"
                    value={b.amount} onChange={e => updateBonus(i, { amount: Number(e.target.value) })}
                    className="w-32 px-2 py-1 text-sm text-right border border-divider rounded bg-surface" />
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={b.is_imponible !== false}
                      onChange={e => updateBonus(i, { is_imponible: e.target.checked })} />
                    Imponible
                  </label>
                  <button onClick={() => removeBonus(i)} className="text-xs text-danger-solid hover:text-danger-content">×</button>
                </div>
              ))}
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-content">Descuentos adicionales</h3>
                <button onClick={addDeduction} className="text-xs px-2 py-1 rounded-md border border-divider hover:bg-soft text-content-muted">
                  + Agregar descuento
                </button>
              </div>
              <p className="text-xs text-content-subtle mb-2">
                Descuentos no previsionales (anticipos, préstamos, etc.). Se restan del líquido.
              </p>
              {deductions.length === 0 && <p className="text-xs italic text-content-subtle">Sin descuentos.</p>}
              {deductions.map((d: any, i: number) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input aria-label={`Nombre del descuento ${i + 1}`} type="text" placeholder="Nombre (ej. Anticipo)"
                    value={d.name} onChange={e => updateDeduction(i, { name: e.target.value })}
                    className="flex-1 px-2 py-1 text-sm border border-divider rounded bg-surface" />
                  <input aria-label={`Monto del descuento ${i + 1}`} type="number" min="0" placeholder="Monto"
                    value={d.amount} onChange={e => updateDeduction(i, { amount: Number(e.target.value) })}
                    className="w-32 px-2 py-1 text-sm text-right border border-divider rounded bg-surface" />
                  <button onClick={() => removeDeduction(i)} className="text-xs text-danger-solid hover:text-danger-content">×</button>
                </div>
              ))}
            </section>
          </div>
        </Modal>
      </td>
    </tr>
  )
}
